import { useState, useEffect, useCallback, useRef } from "react";
import toast from "react-hot-toast";

interface GeolocationState {
  coords: { lat: number; lng: number } | null;
  error: string | null;
  loading: boolean;
}

const MAX_RETRIES = 2; // Reducir reintentos para evitar loops
const RETRY_DELAY = 3000; // Aumentar delay entre reintentos
const LOCATION_TIMEOUT = 20000; // Sincronizar con el timeout de las opciones
// Opciones optimizadas para mejor compatibilidad PC/Móvil
const LOCATION_OPTIONS: PositionOptions = {
  enableHighAccuracy: false,    // Menos agresivo para PC (usa WiFi/red)
  timeout: 15000,              // Reducir timeout para evitar esperas largas
  maximumAge: 30000,           // Permitir cache más largo para evitar solicitudes frecuentes
};

export const useGeolocation = () => {
  const [state, setState] = useState<GeolocationState>({
    coords: null,
    error: null,
    loading: false,
  });

  const watchIdRef = useRef<number | null>(null);
  const retryCountRef = useRef(0);
  const timeoutRef = useRef<NodeJS.Timeout | null>(null);
  const mountedRef = useRef(true);

  // Persistencia de estado de geolocalización
  const GEOLOCATION_STORAGE_KEY = 'geolocation_active';
  const GEOLOCATION_TIMEOUT = 5 * 60 * 1000; // 5 minutos

  // Funciones de persistencia
  const saveGeolocationState = useCallback((isActive: boolean) => {
    try {
      sessionStorage.setItem(GEOLOCATION_STORAGE_KEY, JSON.stringify({
        isActive,
        timestamp: Date.now()
      }));
    } catch (error) {
      console.warn('Error guardando estado de geolocalización:', error);
    }
  }, []);

  const restoreGeolocationState = useCallback((): boolean => {
    try {
      const stored = sessionStorage.getItem(GEOLOCATION_STORAGE_KEY);
      if (stored) {
        const { isActive, timestamp } = JSON.parse(stored);
        // Verificar si no ha pasado mucho tiempo (5 minutos)
        return isActive && (Date.now() - timestamp < GEOLOCATION_TIMEOUT);
      }
    } catch (error) {
      console.warn('Error restaurando estado de geolocalización:', error);
    }
    return false;
  }, [GEOLOCATION_TIMEOUT]);

  const clearGeolocationState = useCallback(() => {
    try {
      sessionStorage.removeItem(GEOLOCATION_STORAGE_KEY);
    } catch (error) {
      console.warn('Error limpiando estado de geolocalización:', error);
    }
  }, []);

  const handleError = useCallback((err: GeolocationPositionError, startGeolocationFn?: () => Promise<void>) => {
    if (!mountedRef.current) return;

    const codeMsg = {
      1: "Permisos denegados. Ve a configuración del navegador y habilita la ubicación para este sitio.",
      2: "Ubicación no disponible. Verifica que tengas GPS activado y buena señal.",
      3: "Tiempo agotado. Verifica tu conexión GPS y que estés al aire libre.",
    }[err.code] ?? "Error al obtener la ubicación";

    if (retryCountRef.current < MAX_RETRIES) {
      retryCountRef.current += 1;
      toast.loading(`Reintentando ubicación (${retryCountRef.current}/${MAX_RETRIES})...`, { 
        duration: RETRY_DELAY,
        icon: "🔄"
      });
      
      // Reintentar automáticamente después del delay
      setTimeout(() => {
        if (mountedRef.current && startGeolocationFn) {
          startGeolocationFn().catch(console.error);
        }
      }, RETRY_DELAY);
      return;
    }

    setState({ coords: null, error: codeMsg, loading: false });
    toast.error(codeMsg, { 
      icon: "📍",
      duration: 8000
    });
  }, []);

  const startGeolocation = useCallback(async (): Promise<void> => {
    if (!navigator.geolocation) {
      const msg = "La geolocalización no está soportada en este navegador";
      setState({ coords: null, error: msg, loading: false });
      toast.error(msg, { icon: "🌍" });
      return;
    }

    // Verificar permisos antes de intentar
    if (navigator.permissions) {
      try {
        const permission = await navigator.permissions.query({ name: 'geolocation' as PermissionName });
        
        if (permission.state === 'denied') {
          const msg = "Permisos de ubicación denegados. Por favor, habilítalos en la configuración del navegador.";
          setState({ coords: null, error: msg, loading: false });
          toast.error(msg, { 
            icon: "🚫",
            duration: 5000
          });
          return;
        }
      } catch {
        // Silenciar error de verificación de permisos
      }
    }

    // Limpiar watch anterior si existe
    if (watchIdRef.current !== null) {
      navigator.geolocation.clearWatch(watchIdRef.current);
      watchIdRef.current = null;
    }

    setState(prev => ({ ...prev, loading: true }));
    
    // Guardar estado de activación
    saveGeolocationState(true);

    return new Promise<void>((resolve, reject) => {
      const timeoutId = setTimeout(() => {
        const errorMsg = "Timeout al obtener ubicación. Verifica que tengas GPS activado y buena señal.";
        handleError({
          code: 3,
          message: errorMsg,
          PERMISSION_DENIED: 1,
          POSITION_UNAVAILABLE: 2,
          TIMEOUT: 3,
        } as GeolocationPositionError, startGeolocation);
        reject(new Error(errorMsg));
      }, LOCATION_TIMEOUT + 1000);

      navigator.geolocation.getCurrentPosition(
        (pos) => {
          clearTimeout(timeoutId);
          const { latitude: lat, longitude: lng } = pos.coords;
          
          setState({ coords: { lat, lng }, error: null, loading: false });
          resolve();

          // Iniciar watch después de obtener la primera ubicación
          watchIdRef.current = navigator.geolocation.watchPosition(
            (watchPos) => {
              setState({ 
                coords: { 
                  lat: watchPos.coords.latitude, 
                  lng: watchPos.coords.longitude 
                }, 
                error: null, 
                loading: false 
              });
            },
            (watchError) => {
              handleError(watchError, startGeolocation);
            },
            LOCATION_OPTIONS
          );
        },
        (error) => {
          clearTimeout(timeoutId);
          handleError(error, startGeolocation);
          reject(error);
        },
        LOCATION_OPTIONS
      );
    });
  }, [handleError, saveGeolocationState]);

  // Cleanup effect
  useEffect(() => {
    const currentTimeout = timeoutRef.current;
    const currentWatchId = watchIdRef.current;

    return () => {
      mountedRef.current = false;
      
      if (currentWatchId !== null) {
        navigator.geolocation.clearWatch(currentWatchId);
      }
      
      if (currentTimeout) {
        clearTimeout(currentTimeout);
      }
    };
  }, []);

  const startLocationTracking = useCallback(async () => {
    if (!state.loading && !state.coords) {
      await startGeolocation();
    }
  }, [state.loading, state.coords, startGeolocation]);

  const stopLocationTracking = useCallback(() => {
    // Limpiar watch
    if (watchIdRef.current !== null) {
      navigator.geolocation.clearWatch(watchIdRef.current);
      watchIdRef.current = null;
    }

    // Limpiar timeout si existe
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }

    // Resetear contador de reintentos
    retryCountRef.current = 0;

    // Limpiar estado de persistencia
    clearGeolocationState();

    // Resetear estado
    setState({ coords: null, error: null, loading: false });
  }, [clearGeolocationState]);

  // TEMPORALMENTE DESHABILITADO: Auto-reactivar geolocalización si estaba activa antes del refresh
  // Esto evita los loops de reintentos en PC
  useEffect(() => {
    const shouldReactivate = restoreGeolocationState();
    
    if (shouldReactivate) {
      // Solo mostrar un mensaje informativo, no auto-reactivar
      console.log('Geolocalización estaba activa antes del refresh, pero auto-reactivación está deshabilitada');
      
      // Limpiar estado guardado para evitar futuros intentos
      clearGeolocationState();
      
      // Mostrar mensaje al usuario de que puede reactivar manualmente
      setTimeout(() => {
        toast('La ubicación se desactivó al refrescar. Puedes reactivarla manualmente.', {
          icon: '🔄',
          duration: 4000,
        });
      }, 1000);
    }
  }, [restoreGeolocationState, clearGeolocationState]); // Solo ejecutar una vez al montar

  return { 
    ...state, 
    startLocationTracking,
    stopLocationTracking,
    isTracking: watchIdRef.current !== null 
  };
};